import { useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { MiniTopic } from '@deepguide-ai/types'
import { useAtom, useSetAtom } from 'jotai'
import localforage from '@/lib/localforageClient'
import { toast } from 'sonner'
import {
  allTopicsFlatAtom,
  allSectionsFlatAtom,
  closeAllDialogsAtom,
  currentParentTopicAtom,
  currentSectionIdAtom,
  currentTopicAtom,
  currentTopicFathersAtom,
  defaultSelectedSectionAtom,
  editorRefAtom,
  generationStatusAtom,
  isAddDialogOpenAtom,
  isCreatingTopicAtom,
  isDeleteDialogOpenAtom,
  isEditDialogOpenAtom,
  isGeneratingArticleAtom,
  isGenerationModeAtom,
  isLiveModeAtom,
  isLoadingTopicsAtom,
  isMockModeAtom,
  isMoveDialogOpenAtom,
  isStreamingAtom,
  lastSavedAtAtom,
  newTopicDescriptionAtom,
  newTopicTitleAtom,
  newTopicTypeAtom,
  openAddDialogAtom,
  openDeleteDialogAtom,
  openEditDialogAtom,
  openMoveDialogAtom,
  sectionChildrenAtom,
  sectionsAtom,
  sectionsForCurrentTopicAtom,
  sectionsWithChildrenAtom,
  selectedSectionIdAtom,
  selectedTopicAtom,
  selectedTopicIdAtom,
  topicTreeAtom,
  topicsCountAtom,
  topicsHierarchyAtom,
} from '@/stores/topicStore'
import { workspaceIdAtom } from '@/stores/workspaceStore'
import { queryClient, trpc } from '@/lib/trpc'

// Hook that provides the same interface as the old TopicContext but uses jotai atoms
export function useTopicAtomsContext() {
  const [workspaceId] = useAtom(workspaceIdAtom)
  // Atom setters for tRPC integration
  // const setTopicsHierarchy = useSetAtom(topicsHierarchyAtom)
  // const setIsLoadingTopics = useSetAtom(isLoadingTopicsAtom)
  const setCurrentTopic = useSetAtom(currentTopicAtom)
  const [selectedTopicId] = useAtom(selectedTopicIdAtom)

  // Query moved to useTopicHierarchyQuery() singleton - this hook just reads atoms

  // Invalidate topics hierarchy
  const invalidateTopicHierarchy = async () => {
    await queryClient.invalidateQueries({
      queryKey: trpc.topics.getWorkspaceTopicHierarchy.queryKey({
        workspaceId: workspaceId!,
      }),
    })
    console.log('✅ Topic hierarchy invalidation complete')
  }

  // tRPC Mutations
  const createTopicMutation = useMutation(
    trpc.topics.createTopic.mutationOptions({})
  )

  const updateTopicMutation = useMutation(
    trpc.topics.updateTopic.mutationOptions({
      onSuccess: async () => {
        await invalidateTopicHierarchy()
      },
      onError: (error: Error) => {
        toast.error(`Failed to update topic: ${error.message}`)
      },
    })
  )

  const deleteTopicMutation = useMutation(
    trpc.topics.deleteTopic.mutationOptions({
      onSuccess: async () => {
        const docId = currentTopic?.docId
        setIsDeleteDialogOpen(false)
        setCurrentTopic(null)
        // also delete from local storage
        if (docId) {
          localforage.removeItem(docId)
        }
        // Invalidate topic hierarchy to refresh the tree data
        await invalidateTopicHierarchy()
      },
      onError: (error: Error) => {
        toast.error(`Failed to delete topic: ${error.message}`)
      },
    })
  )

  const moveTopicMutation = useMutation(
    trpc.topics.moveTopic.mutationOptions({
      onSuccess: async (topic: MiniTopic) => {
        const topicId = topic.id
        toast.success('Topic moved successfully')
        setIsDeleteDialogOpen(false)
        await invalidateTopicHierarchy()
        setTimeout(() => {
          setSelectedTopicId(topicId)
        }, 200)
      },
      onError: (error: Error) => {
        toast.error(`Failed to move topic: ${error.message}`)
      },
    })
  )

  const updateTopicHierarchyMutation = useMutation(
    trpc.topics.updateTopicHierarchy.mutationOptions({
      onSuccess: async () => {
        await invalidateTopicHierarchy()
      },
      onError: (error: Error) => {
        toast.error(`Failed to update: ${error.message}`)
      },
    })
  )

  const updateStepMutation = useMutation(
    trpc.steps.updateStep.mutationOptions({})
  )

  // Read atoms (these automatically subscribe to changes)
  const [topicsHierarchy] = useAtom(topicsHierarchyAtom)
  const [allTopicsFlat] = useAtom(allTopicsFlatAtom)
  const [selectedTopic] = useAtom(selectedTopicAtom)
  const [topicTree] = useAtom(topicTreeAtom)
  const [, setSelectedTopicId] = useAtom(selectedTopicIdAtom)
  const [, setCurrentTopicFathers] = useAtom(currentTopicFathersAtom)
  const [topicsCount] = useAtom(topicsCountAtom)
  const [lastSavedAt, setLastSavedAt] = useAtom(lastSavedAtAtom)
  const [isLoadingTopics] = useAtom(isLoadingTopicsAtom)

  // Generation state atoms - new streaming-optimized atoms
  const [isGenerationMode, setIsGenerationMode] = useAtom(isGenerationModeAtom)
  const [generationStatus, setGenerationStatus] = useAtom(generationStatusAtom)
  const [isStreaming] = useAtom(isStreamingAtom)
  const [isMockMode, setIsMockMode] = useAtom(isMockModeAtom)

  // Derived generation state atoms (read-only)
  const [isCreatingTopic] = useAtom(isCreatingTopicAtom)
  const [isGeneratingArticle] = useAtom(isGeneratingArticleAtom)

  // Legacy atoms
  const [isLiveMode, setIsLiveMode] = useAtom(isLiveModeAtom)
  const [editorRef, setEditorRef] = useAtom(editorRefAtom)

  // Dialog state atoms
  const [isAddDialogOpen, setIsAddDialogOpen] = useAtom(isAddDialogOpenAtom)
  const [isEditDialogOpen, setIsEditDialogOpen] = useAtom(isEditDialogOpenAtom)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useAtom(
    isDeleteDialogOpenAtom
  )
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useAtom(isMoveDialogOpenAtom)
  const [currentParentTopic] = useAtom(currentParentTopicAtom)
  const [currentSectionId] = useAtom(currentSectionIdAtom)
  const [currentTopic] = useAtom(currentTopicAtom)
  const [newTopicTitle, setNewTopicTitle] = useAtom(newTopicTitleAtom)
  const [newTopicType, setNewTopicType] = useAtom(newTopicTypeAtom)
  const [newTopicDescription, setNewTopicDescription] = useAtom(
    newTopicDescriptionAtom
  )

  // Action atoms (write-only)
  const closeAllDialogs = useSetAtom(closeAllDialogsAtom)
  const openAddDialog = useSetAtom(openAddDialogAtom)
  const openEditDialog = useSetAtom(openEditDialogAtom)
  const openDeleteDialog = useSetAtom(openDeleteDialogAtom)
  const openMoveDialog = useSetAtom(openMoveDialogAtom)
  const setCurrentTopicAction = useSetAtom(currentTopicAtom)
  const setTopicsHierarchyAction = useSetAtom(topicsHierarchyAtom)

  // Section atoms
  const [sections] = useAtom(sectionsAtom)
  const [sectionsWithChildren] = useAtom(sectionsWithChildrenAtom)
  const [allSectionsFlat] = useAtom(allSectionsFlatAtom)
  const [sectionChildren] = useAtom(sectionChildrenAtom)
  const [sectionsForCurrentTopic] = useAtom(sectionsForCurrentTopicAtom)
  const [selectedSectionId, setSelectedSectionId] = useAtom(selectedSectionIdAtom)
  const [defaultSelectedSection] = useAtom(defaultSelectedSectionAtom)

  // Action handlers
  const handleAddTopic = (parentTopic: MiniTopic | null = null, sectionId?: string, templateId?: string | null) => {
    openAddDialog(parentTopic, sectionId, templateId)
  }

  const handleEditTopic = (topic: MiniTopic) => {
    openEditDialog(topic)
  }

  const handleDeleteTopic = (topic: MiniTopic) => {
    openDeleteDialog(topic)
  }

  const handleMoveTopic = (topic: MiniTopic) => {
    openMoveDialog(topic)
  }
  useEffect(() => {
    const parentNames: MiniTopic[] = []

    if (selectedTopicId) {
      let currentTopicId = selectedTopicId
      while (currentTopicId) {
        const topic = allTopicsFlat.find((t) => t.id === currentTopicId)
        if (topic) {
          parentNames.unshift(topic) // 添加到数组开头，保持层级顺序
          currentTopicId = topic.parentId || ''
        } else {
          break
        }
      }
    }
    setCurrentTopicFathers(parentNames)
  }, [selectedTopicId, allTopicsFlat, setCurrentTopicFathers])

  return {
    // Core data (from atoms)
    topicsHierarchy,
    allTopicsFlat,
    selectedTopic,
    topicTree,
    selectedTopicId,
    topicsCount,
    lastSavedAt,
    isLoadingTopics,

    // State setters
    setSelectedTopicId,
    setLastSavedAt,
    setTopicsHierarchy: setTopicsHierarchyAction,

    // Streaming-optimized generation state
    isGenerationMode,
    setIsGenerationMode,
    generationStatus,
    setGenerationStatus,
    isStreaming,
    isMockMode,
    setIsMockMode,

    // Derived generation state (read-only)
    isCreatingTopic,
    isGeneratingArticle,

    // Legacy state
    isLiveMode,
    setIsLiveMode,

    // Editor ref
    editorRef,
    setEditorRef,

    // Dialog state
    isAddDialogOpen,
    isEditDialogOpen,
    isDeleteDialogOpen,
    isMoveDialogOpen,
    currentParentTopic,
    currentSectionId,
    currentTopic,
    newTopicTitle,
    newTopicDescription,
    newTopicType,
    setIsAddDialogOpen,
    setIsEditDialogOpen,
    setIsDeleteDialogOpen,
    setIsMoveDialogOpen,
    setNewTopicTitle,
    setNewTopicDescription,
    setNewTopicType,
    setCurrentTopic: setCurrentTopicAction,

    // Action handlers
    handleAddTopic,
    handleEditTopic,
    handleDeleteTopic,
    handleMoveTopic,
    closeAllDialogs,

    // Section data
    sections,
    sectionsWithChildren,
    allSectionsFlat,
    sectionChildren,
    sectionsForCurrentTopic,
    selectedSectionId,
    setSelectedSectionId,
    defaultSelectedSection,

    // Mutations
    createTopicMutation,
    updateTopicMutation,
    deleteTopicMutation,
    moveTopicMutation,
    updateTopicHierarchyMutation,
    updateStepMutation,
    refreshTopics: invalidateTopicHierarchy,
    invalidateTopicHierarchy,
  }
}
